import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { Agent } from '@conversia/shared-types';
import * as agentsApi from '@/api/agents.api';
import * as installationApi from '@/api/installation.api';
import { ApiError } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/auth.store';

const schema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(120),
  description: z.string().max(500).optional(),
  systemPrompt: z.string().min(1, 'El system prompt es obligatorio'),
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
  engineType: z.enum(['api', 'cli']),
  // '' = sin modelo propio (usa el default de la instalación).
  model: z.string(),
  // Permiso fino del motor CLI (solo ADMIN; el server también lo exige).
  cliAllowed: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function AgentFormModal({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent?: Agent | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(agent);

  // El selector de MOTOR solo existe en instalaciones internas de Silva Notion
  // (la vista CRM lo dice; la API además lo rechaza con 422 si no lo es).
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
    enabled: open,
  });
  const isInternal = crmView?.isInternal === true;
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';

  // Catálogo de modelos con descripciones llanas (configurable en el server).
  const { data: modelCatalog } = useQuery({
    queryKey: ['agents', 'models'],
    queryFn: agentsApi.getModelCatalog,
    enabled: open,
  });
  const modelos = modelCatalog?.modelos ?? [];

  // Whitelist de contactos: lista de strings gestionada fuera de RHF (tag input).
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [whitelistDraft, setWhitelistDraft] = useState('');

  const addWhitelistName = () => {
    const value = whitelistDraft.trim();
    if (!value) return;
    setWhitelist((prev) =>
      prev.some((n) => n.toLowerCase() === value.toLowerCase()) ? prev : [...prev, value],
    );
    setWhitelistDraft('');
  };
  const removeWhitelistName = (name: string) =>
    setWhitelist((prev) => prev.filter((n) => n !== name));

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      systemPrompt: '',
      status: 'ACTIVE',
      engineType: 'api',
      model: '',
      cliAllowed: false,
    },
  });
  const cliAllowed = watch('cliAllowed');

  useEffect(() => {
    if (open) {
      reset({
        name: agent?.name ?? '',
        description: agent?.description ?? '',
        systemPrompt: agent?.systemPrompt ?? '',
        status: agent?.status ?? 'ACTIVE',
        engineType: agent?.engineType ?? 'api',
        model: agent?.model ?? '',
        cliAllowed: agent?.cliAllowed ?? false,
      });
      setWhitelist(Array.isArray(agent?.contactWhitelistJson) ? agent.contactWhitelistJson : []);
      setWhitelistDraft('');
    }
  }, [open, agent, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // El motor solo viaja cuando la instalación es interna (donde el
      // selector existe); en instalaciones de clientes ni se envía el campo.
      // Motor efectivo: sin permiso CLI no viaja 'cli' (el server igual lo
      // rechazaría con 422; la UI ya lo fuerza a 'api').
      const engineType = isInternal
        ? values.cliAllowed
          ? values.engineType
          : 'api'
        : undefined;
      // cliAllowed solo lo puede mandar un ADMIN en instalación interna.
      const cliAllowedPayload = isInternal && isAdmin ? { cliAllowed: values.cliAllowed } : {};
      // '' = sin modelo propio → null (usa el default de la instalación).
      const model = values.model || null;
      if (isEdit && agent) {
        return agentsApi.updateAgent(agent.id, {
          name: values.name,
          description: values.description || undefined,
          systemPrompt: values.systemPrompt,
          status: values.status,
          contactWhitelist: whitelist,
          model,
          ...(engineType ? { engineType } : {}),
          ...cliAllowedPayload,
        });
      }
      return agentsApi.createAgent({
        name: values.name,
        description: values.description || undefined,
        systemPrompt: values.systemPrompt,
        contactWhitelist: whitelist,
        model,
        ...(engineType ? { engineType } : {}),
        ...cliAllowedPayload,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      toast.success(isEdit ? 'Agente actualizado' : 'Agente creado');
      onClose();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'No se pudo guardar el agente';
      setError('root', { message });
    },
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isEdit ? 'Editar agente' : 'Nuevo agente'}
      description="Define el comportamiento del agente AI mediante su system prompt."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear agente'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre" placeholder="Asistente de ventas" error={errors.name?.message} {...register('name')} />
        <Input
          label="Descripción"
          placeholder="Atiende consultas de productos y precios"
          error={errors.description?.message}
          {...register('description')}
        />
        <Textarea
          label="Instrucciones del asistente (prompt)"
          rows={10}
          placeholder="Eres un asistente de ventas amable…"
          error={errors.systemPrompt?.message}
          {...register('systemPrompt')}
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Lista de nombres permitidos (whitelist)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {whitelist.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
              >
                {name}
                <button
                  type="button"
                  onClick={() => removeWhitelistName(name)}
                  className="rounded-full p-0.5 hover:bg-brand-200 dark:hover:bg-brand-800"
                  aria-label={`Quitar ${name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Input
              placeholder="Agregar nombre (ej: Bashi)"
              value={whitelistDraft}
              onChange={(e) => setWhitelistDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addWhitelistName();
                }
              }}
            />
            <Button type="button" variant="outline" onClick={addWhitelistName}>
              Agregar
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            💡 Si la lista está vacía, el agente responde a todos. Si tiene nombres, sólo responde a
            contactos cuyo nombre contenga alguno de estos textos (sin distinguir mayúsculas).
          </p>
        </div>

        <div>
          <Select
            label="Modelo de IA (motor API)"
            error={errors.model?.message}
            options={[
              { value: '', label: 'Default de la instalación (recomendado si no sabés cuál)' },
              ...modelos.map((m) => ({
                value: m.id,
                label: `${m.nombre} — ${m.descripcion}`,
              })),
            ]}
            {...register('model')}
          />
          <p className="mt-1 text-xs text-slate-400">
            Con qué "cerebro" responde este agente. Cambiarlo no toca su prompt ni su
            comportamiento configurado, solo el modelo que lo ejecuta.
          </p>
        </div>

        {isInternal && (
          <div className="space-y-2 rounded-lg border border-surface-border p-3 dark:border-slate-700">
            {isAdmin && (
              <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={cliAllowed}
                  onChange={(e) => {
                    setValue('cliAllowed', e.target.checked);
                    // Sin permiso no hay motor CLI que mantener.
                    if (!e.target.checked) setValue('engineType', 'api');
                  }}
                />
                <span>
                  <span className="font-medium">Permitir motor CLI</span> — usa la suscripción
                  Max configurada, no la API. Solo un administrador puede cambiarlo.
                </span>
              </label>
            )}
            <Select
              label="Motor de IA"
              error={errors.engineType?.message}
              disabled={!cliAllowed}
              options={[
                { value: 'api', label: 'API Anthropic (BYOK) — estándar' },
                { value: 'cli', label: 'CLI — suscripción Max vía VPS (interno)' },
              ]}
              {...register('engineType')}
            />
            <p className="text-xs text-slate-400">
              {cliAllowed
                ? 'El motor CLI usa la suscripción del equipo en el VPS interno (requiere el vps-agent configurado).'
                : "Para elegir el motor CLI, primero activá 'Permitir motor CLI' (solo admin)."}
            </p>
          </div>
        )}

        {isEdit && (
          <Select
            label="Estado"
            error={errors.status?.message}
            options={[
              { value: 'ACTIVE', label: 'Activo' },
              { value: 'PAUSED', label: 'Pausado' },
              { value: 'ARCHIVED', label: 'Archivado' },
            ]}
            {...register('status')}
          />
        )}
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}
